import { LitElement, css, html } from 'lit';

const themes = ['system', 'light', 'dark'] as const;
type Theme = (typeof themes)[number];

class FangmoThemeToggle extends LitElement {
  static properties = {
    theme: { state: true }
  };

  static styles = css`
    :host {
      display: inline-flex;
    }

    button {
      appearance: none;
      border: 1px solid var(--color-border, #d7dcd9);
      border-radius: 999px;
      background: var(--color-surface, #fff);
      color: var(--color-text, #17201b);
      cursor: pointer;
      font: inherit;
      font-size: 0.85rem;
      padding: 0.55rem 0.85rem;
    }

    button:focus-visible {
      outline: 3px solid color-mix(in srgb, var(--color-accent, #0b7a53) 30%, transparent);
      outline-offset: 2px;
    }
  `;

  declare private theme: Theme;

  constructor() {
    super();
    this.theme = 'system';
  }

  connectedCallback() {
    super.connectedCallback();
    try {
      const saved = window.localStorage.getItem('fangmo-theme');
      this.theme = themes.includes(saved as Theme) ? (saved as Theme) : 'system';
    } catch {
      this.theme = 'system';
    }
    this.applyTheme();
  }

  private cycleTheme() {
    const index = themes.indexOf(this.theme);
    this.theme = themes[(index + 1) % themes.length] ?? 'system';
    this.applyTheme();
    try {
      window.localStorage.setItem('fangmo-theme', this.theme);
    } catch {
      // Storage may be disabled; the selected theme still applies to this page.
    }
  }

  private applyTheme() {
    document.documentElement.dataset.theme = this.theme;
  }

  render() {
    // The server picks the page language; the toggle follows <html lang>.
    const english = document.documentElement.lang.startsWith('en');
    const labels: Record<Theme, string> = english
      ? { system: 'system', light: 'light', dark: 'dark' }
      : { system: '跟随系统', light: '浅色', dark: '深色' };

    return html`
      <button
        type="button"
        aria-label=${
          english
            ? `Switch colour theme, currently ${labels[this.theme]}`
            : `切换颜色主题，当前为${labels[this.theme]}`
        }
        @click=${() => this.cycleTheme()}
      >
        ${english ? `Theme: ${labels[this.theme]}` : `主题：${labels[this.theme]}`}
      </button>
    `;
  }
}

if (!customElements.get('fangmo-theme-toggle')) {
  customElements.define('fangmo-theme-toggle', FangmoThemeToggle);
}
